import { useLayoutEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Textarea'
import CellTabBar from './CellTabBar'
import { CommentRow } from './RowCommentThread'
import { type CellTab } from './reviewComments'
import { fitTextareaHeight } from '@/lib/fitTextareaHeight'

const noop = () => {}

// Tallest the comment box grows before it scrolls (about twelve lines).
const MAX_BOX_PX = 280

// The review comments under one row of the Plan changes tab: one tab per
// commented cell, each with only that cell's thread and a comment box.
// Overall Plan rows have one commentable number, so they skip the tab bar
// and show the cell's name instead.
export default function CellCommentTabs({
  tabs, active, withTabBar, readOnly, draft, onDraftChange, onSelect, onClose, onCreate, onUpdate, onDelete,
}: {
  // Commented cells, plus the picked cell when it has no comments yet.
  tabs: CellTab[]
  active: string | null
  withTabBar: boolean
  readOnly: boolean
  draft: string
  onDraftChange: (body: string) => void
  onSelect: (columnKey: string) => void
  onClose: () => void
  onCreate: (body: string) => void
  onUpdate: (id: string, body: string) => void
  onDelete: (id: string) => void
}) {
  const tab = tabs.find((t) => t.column_key === active) ?? null
  const boxRef = useRef<HTMLTextAreaElement>(null)
  // Fit the box to the text, also when switching tabs or after posting clears it.
  useLayoutEffect(() => {
    if (boxRef.current) fitTextareaHeight(boxRef.current, MAX_BOX_PX)
  }, [draft, active])
  const close = !readOnly && tab && (
    <button type="button" onClick={onClose} className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-foreground hover:bg-muted">
      <X size={12} aria-hidden />Close
    </button>
  )
  return (
    <div className="bg-muted/40 px-3 py-2.5">
      <div className="rounded-lg border border-border bg-background">
        {withTabBar ? (
          <CellTabBar
            tabs={tabs.map((t) => ({ key: t.column_key, label: t.label, count: t.comments.length }))}
            active={active}
            onSelect={onSelect}
            trailing={close}
          />
        ) : tab && (
          <div className="flex items-center gap-3 border-b border-border px-4 py-2">
            <span className="text-sm font-semibold text-foreground">{tab.label}</span>
            {close}
          </div>
        )}
        {tab ? (
          <>
            {tab.comments.length > 0 && (
              <div className="divide-y divide-border">
                {tab.comments.map((c) => (
                  <div key={c.id} className="px-4 py-2.5">
                    <CommentRow comment={c} readOnly={readOnly} withContext={false}
                      onUpdate={readOnly ? noop : onUpdate} onDelete={readOnly ? noop : onDelete} />
                  </div>
                ))}
              </div>
            )}
            {!readOnly && (
              <div className={`flex flex-col gap-2 px-4 py-3 ${tab.comments.length > 0 ? 'border-t border-border' : ''}`}>
                <Textarea
                  ref={boxRef}
                  rows={2}
                  className="resize-none overflow-y-hidden focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-chart-muted"
                  placeholder={tab.comments.length > 0 ? 'Reply…' : `Add a comment on ${tab.label}…`}
                  value={draft}
                  onChange={(e) => onDraftChange(e.target.value)}
                />
                <div className="flex justify-end">
                  <Button size="sm" disabled={!draft.trim()} onClick={() => onCreate(draft.trim())}>Comment</Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="px-4 py-2.5 text-sm text-foreground">Click a tab to read its comments.</p>
        )}
      </div>
    </div>
  )
}
